// Imports => Vendor
import axios from "axios";

// Imports => Interceptors
import { AcTokenRefresher } from "./interceptors.api";

// Imports => Utilities
import { AcIsSet } from "@opp/ui-kit";
import { AcGetAccessToken, AcGetTokenType } from "@utils";

// Imports => API
import BankAccountsAPI from "@api/bankaccounts.api";
import GlobalAPI from "@api/global.api";
import ProfileAPI from "@api/profile.api";
import UserAPI from "@api/user.api";
import ComplianceRequirementApi from "@api/compliance_requirements.api";
import TicketsAPI from "@api/tickets.api";
import TransactionsAPI from "@api/transactions.api";
import MovementsAPI from "@api/movements.api";
import WithdrawalsAPI from "@api/withdrawals.api";
import PurchasesAPI from "@api/purchases.api";
import BalanceAPI from "@api/balance.api";
import SettingsAPI from "@api/settings.api";
import ReportingAPI from "@api/reporting.api";
import rateLimit from "axios-rate-limit";

/*
const onUploadProgress = (event) => {
    console.group('[Axios] => fn.onUploadProgress');
    console.log('Event: ', event);
    console.groupEnd();
};

const onDownloadProgress = (event) => {
    console.group('[Axios] => fn.onDownloadProgress');
    console.log('Event: ', event);
    console.groupEnd();
};
*/
let _timeOut = null;
let _errorTokens = [];
let _isRefreshing = false;
let _requestQueue = [];

const unauthenticatedState = (state) => {
  const unauthenticatedEvent = new CustomEvent("unAuthenticate");
  window.dispatchEvent(unauthenticatedEvent);
};

const processRequestQueue = (error, token = null) => {
  const collection = _requestQueue;
  const len = collection.length;
  let n = 0;

  for (n; n < len; n++) {
    const instance = collection[n];

    if (AcIsSet(error)) {
      instance.reject(error);
    } else {
      instance.resolve(token);
    }
  }

  _requestQueue = [];
};

export const addInterceptors = (requestClient) => {
  requestClient.interceptors.response.use(
    (response) => response,
    (error) => {
      clearTimeout(_timeOut);
      const originalRequest = error.config;

      const isNoBearerTokenError =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error === "no-bearer-token";

      if (
        error &&
        error.response &&
        error.response.status !== 401 &&
        !isNoBearerTokenError
      ) {
        return Promise.reject(error);
      } else if (
        error &&
        error.response &&
        (error.response.status === 401 || isNoBearerTokenError) &&
        !originalRequest._retry
      ) {
        if (_isRefreshing) {
          return new Promise((resolve, reject) => {
            _requestQueue.push({ resolve, reject });
          })
            .then((token) => {
              const access_token = AcGetAccessToken();
              const token_type = AcGetTokenType() || "Bearer";

              originalRequest.headers[
                "authorization"
              ] = `${token_type} ${access_token}`;

              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        } else {
          originalRequest._retry = true;
          _isRefreshing = true;

          return new Promise((resolve, reject) => {
            AcTokenRefresher(requestClient, unauthenticatedState)
              .then((response) => {
                const access_token = AcGetAccessToken();
                const token_type = AcGetTokenType() || "Bearer";

                originalRequest.headers[
                  "authorization"
                ] = `${token_type} ${access_token}`;

                if (_requestQueue && _requestQueue.length > 0)
                  processRequestQueue(null, `${token_type} ${access_token}`);

                resolve(axios(originalRequest));
              })
              .catch((error) => {
                // Failed to refresh the token
                clearTimeout(_timeOut);

                if (_requestQueue && _requestQueue.length > 0)
                  processRequestQueue(error, `${token_type} ${access_token}`);

                // _timeOut = setTimeout(() => unauthenticatedState(false), 500);

                reject(error);
              })
              .finally(() => {
                _isRefreshing = false;
              });
          });
        }
      }

      return Promise.reject(error);
    }
  );

  requestClient.interceptors.request.use(async (config) => {
    // await AcTokenRefresher(requestClient, unauthenticatedState);

    const source = axios.CancelToken.source();
    config.cancelToken = source.token;
    _errorTokens.push(source);

    return config;
  });
};

export const addErrorLoggingInterceptors = (
  requestClient,
  errorLoggingClient
) => {
  // Add logging
  requestClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error &&
        error.name &&
        error.response &&
        error.request &&
        error.request.responseURL &&
        error.response.data &&
        error.response.status !== 400 && // Bad request
        error.response.status !== 401 && // Unauthorized
        error.response.status !== 404 && // Not found
        error.response.status !== 429 &&   // Too many requests
        error.response.data?.error !== 'api-client-response-error' // These are handled errors in merchant api
      ) {
        let errorReport = {
          component: error.name,
          message: error.message,
          location: error.request.responseURL,
          stack: error.response.data,
        };

        errorLoggingClient.report(errorReport);
      }

      return Promise.reject(error);
    }
  );
};

export default (config) => {
  axios.defaults.withCredentials = true;

  const ReportingClient = rateLimit(
    axios.create({
      ...config.api,
    }),
    { maxRequests: 3, perMilliseconds: 10000 }
  );
  addInterceptors(ReportingClient);

  const reporting = new ReportingAPI(ReportingClient, config);

  const NoAuthClient = axios.create({
    ...config.api_no_auth,
  });

  const Client = axios.create({
    ...config.api,
  });
  addInterceptors(Client);
  addErrorLoggingInterceptors(Client, reporting);

  const PlainClient = axios.create({
    ...config.api_plain,
  });
  addInterceptors(PlainClient);
  addErrorLoggingInterceptors(PlainClient, reporting);

  const DownloadClient = axios.create({
    ...config.api_download,
  });
  addInterceptors(DownloadClient);
  addErrorLoggingInterceptors(DownloadClient, reporting);

  const UploadClient = axios.create({
    ...config.api_upload,
  });
  addInterceptors(UploadClient);
  addErrorLoggingInterceptors(UploadClient, reporting);

  const NoBaseClient = axios.create({ ...config.api_no_base });
  addInterceptors(NoBaseClient);
  addErrorLoggingInterceptors(NoBaseClient, reporting);

  const global = new GlobalAPI(Client, config);
  const profile = new ProfileAPI(Client, NoBaseClient, config);
  const user = new UserAPI(Client, NoBaseClient, config);

  const bank_accounts = new BankAccountsAPI(Client, config);
  // const settlements = new SettlementsAPI(Client, config);
  const tickets = new TicketsAPI(Client, config);
  const transactions = new TransactionsAPI(Client, config);
  const movements = new MovementsAPI(Client, config);
  const withdrawals = new WithdrawalsAPI(Client, config);
  const purchases = new PurchasesAPI(Client, config);

  const balance = new BalanceAPI(Client, config);

  const compliance_requirements = new ComplianceRequirementApi(Client, config);
  const settings = new SettingsAPI(Client, config);

  return {
    bank_accounts,
    global,
    profile,
    user,
    tickets,
    transactions,
    movements,
    withdrawals,
    purchases,
    balance,
    compliance_requirements,
    settings,
    reporting,
  };
};
